import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  doSetupBillingAdminUpdateItem,
  eventSelectScreenSelection,
} from 'store/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual, sortBy, toInteger } from 'lodash';
import { useSelector } from 'hooks/useSelector';
import {
  selectBillingItem,
  selectRemoteCustomers,
  selectRemoteOrganisationId,
} from 'store/remote/selectors';
import { BillingAccountEnum, BillingItem } from 'types/Billing';
import { TextField, TextFieldSpecial } from 'components/Field/TextField';
import { CardRow } from 'components/CardRow';
import { useAlert } from 'hooks/useAlert';
import { formatDate, randomKey } from 'lib/riskbase';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { ICONS } from 'components/Icon';
import { theme } from 'lib/theme';
import { MonthEnum, Months } from 'types/types';
import { smartText } from 'lib/str';
import { addYears, subMonths } from 'date-fns';

const DefaultItem: BillingItem = {
  id: '',
  name: '',
  cost: 0,
  vat: 20,
  invoiceCode: '',
  months: [MonthEnum.January],
  repeating: false,
  account: BillingAccountEnum.licensing,
  invoiceToOrganisationId: null,
};

export const BillingItemModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsBillingItem>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const { itemId } = route.params;

  const eventKeyOrganisationRef = React.useRef(randomKey());

  const defaultItem = React.useMemo(() => {
    const now = new Date();
    const nextMonthNumber =
      new Date(now.getFullYear(), now.getMonth() + 1, 1).getMonth() + 1;
    return {
      ...DefaultItem,
      months: [
        Months.find(({ number }) => number === nextMonthNumber)?.name ||
          MonthEnum.January,
      ],
    };
  }, []);

  const product =
    useSelector((state) => selectBillingItem(state, itemId || '-1')) ||
    defaultItem;

  const customers = useSelector(selectRemoteCustomers) || {};
  const organisationId = useSelector(selectRemoteOrganisationId);
  const dataRef = React.useRef<BillingItem>(cloneDeep(product));
  const negativeRef = React.useRef<boolean>(dataRef.current.cost < 0);
  const isDirty = React.useCallback(
    () => !isEqual(product, dataRef.current),
    [product],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const invoiceTo = customers[organisationId || '-1'];
  const invoiceToOverride =
    customers[dataRef.current.invoiceToOrganisationId || '-1'];

  const handleSave = React.useCallback(() => {
    const errors: string[] = [];
    if (!dataRef.current.name.trim()) {
      errors.push('Name is required');
    }
    if (!dataRef.current.cost) {
      errors.push('Cost is required and must be more than £0.00');
    }
    if (errors.length) {
      alert({
        text: errors.join('. '),
      });
      return;
    }
    dispatch(doSetupBillingAdminUpdateItem(dataRef.current));
  }, [alert, dispatch]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () =>
          dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingItem])),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingItem]));
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;
    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  const toggleMonth = (month: MonthEnum) => {
    if (!dataRef.current.repeating) {
      dataRef.current.months = [month];
    } else if (dataRef.current.months.includes(month)) {
      dataRef.current.months = dataRef.current.months.filter(
        (m) => m !== month,
      );
    } else {
      const newMonthArray = [...dataRef.current.months, month];
      dataRef.current.months = Months.filter((m) =>
        newMonthArray.includes(m.name),
      ).map((m) => m.name);
    }
    forceUpdate();
  };

  const handleToggleRepeating = (newRepeating: boolean) => {
    dataRef.current.repeating = newRepeating;
    if (!newRepeating && dataRef.current.months.length) {
      dataRef.current.months = [dataRef.current.months[0]!];
    }
    forceUpdate();
  };

  const handleToggleNegative = () => {
    negativeRef.current = !negativeRef.current;
    dataRef.current.cost = negativeRef.current
      ? -dataRef.current.cost
      : Math.abs(dataRef.current.cost);
    forceUpdate();
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (action) => {
        if (action.payload.key === eventKeyOrganisationRef.current) {
          dataRef.current.invoiceToOrganisationId =
            action.payload.stringValues[0] || null;
          forceUpdate();
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [forceUpdate]);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Item"
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card
        explanationLinkText="See {M3}, {M6}, {M12} templates"
        onExplanationLinkPress={() =>
          alert({
            title: '{Mx} Templates',
            text: smartText(
              '*"Task Management ({M3})"*\n' +
                `"Task Management\n(October to December)"` +
                '\n--\n' +
                '*"Task Management ({M12})"*\n' +
                `"Task Management\n(December ${formatDate(new Date(), 'Y')} ` +
                `to November ${formatDate(
                  addYears(subMonths(new Date(), 1), 1),
                  'Y',
                )})"`,
            ),
          })
        }
      >
        <TextField
          placeholder="Item Name"
          onChange={(value) => handleChangeValue('name', value)}
          value={dataRef.current.name}
          onEsc={handleClose}
          autoFocus={!dataRef.current.id}
          autoFocusWeb
          autoFocusDelay
        />
      </Card>
      <Card explanation="Use invoice codes to split items into different invoices">
        <TextField
          textLeft="Invoice Code"
          placeholder="default"
          onChange={(value) => handleChangeValue('invoiceCode', value)}
          value={
            dataRef.current.invoiceCode !== 'default'
              ? dataRef.current.invoiceCode
              : ''
          }
          onEsc={handleClose}
        />
      </Card>

      <Card
        label="Cost (excl. VAT)"
        contentStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}
        marginBottomQuarter
      >
        <TextField
          placeholder="0.00"
          onChange={(value) =>
            handleChangeValue(
              'cost',
              negativeRef.current
                ? -toInteger(value || '0')
                : toInteger(value || '0'),
            )
          }
          textLeft={negativeRef.current ? '-£' : '£'}
          indentActions
          value={`${Math.abs(dataRef.current.cost)}`}
          special={TextFieldSpecial.currency}
          onEsc={handleClose}
          containerStyle={{ flex: 1 }}
        />
        <CardRow
          placeholder={`${dataRef.current.vat}% VAT`}
          style={{ backgroundColor: theme.colors.white }}
          menuOptions={[
            negativeRef.current
              ? {
                  leftIcon: ICONS.PlusLight,
                  text: 'Positive Value',
                  subText: 'Make the cost a positive value',
                  onPress: handleToggleNegative,
                }
              : {
                  leftIcon: ICONS.MinusLight,
                  text: 'Negative Value',
                  subText: 'Make the cost a negative value',
                  onPress: handleToggleNegative,
                },
          ]}
        />
        {/* <TextField
          placeholder="0"
          onChange={(value) =>
            handleChangeValue('vat', toInteger(value || '0'))
          }
          textRight="% VAT"
          indentActions
          value={`${dataRef.current.vat}`}
          special={TextFieldSpecial.number}
          maxNumberValue={100}
          onEsc={handleClose}
          inputStyle={{ textAlign: 'right', paddingRight: 6 }}
          containerStyle={{ width: 100, paddingRight: theme.paddingHorizontal }}
        /> */}
      </Card>

      <Card
        contentStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}
        marginBottomQuarter
      >
        <CardRow
          label="Licensing"
          style={{ width: '50%' }}
          radioValue={dataRef.current.account === BillingAccountEnum.licensing}
          onPress={() =>
            handleChangeValue('account', BillingAccountEnum.licensing, true)
          }
        />
        <CardRow
          label="Rebilling"
          style={{ width: '50%' }}
          radioValue={dataRef.current.account === BillingAccountEnum.rebilling}
          onPress={() =>
            handleChangeValue('account', BillingAccountEnum.rebilling, true)
          }
        />
      </Card>

      <Card
        contentStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}
        marginBottomQuarter
      >
        <CardRow
          label="One Off"
          style={{ width: '50%' }}
          radioValue={!dataRef.current.repeating}
          onPress={() => handleToggleRepeating(false)}
        />
        <CardRow
          label="Repeating"
          style={{ width: '50%' }}
          radioValue={dataRef.current.repeating}
          onPress={() => handleToggleRepeating(true)}
        />
      </Card>

      <Card contentStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {Months.map((m) => (
          <CardRow
            key={m.number}
            label={m.name}
            radioValue={
              dataRef.current.repeating
                ? undefined
                : dataRef.current.months.includes(m.name)
            }
            checkedValue={
              dataRef.current.repeating
                ? dataRef.current.months.includes(m.name)
                : undefined
            }
            onPress={() => toggleMonth(m.name)}
            style={{ width: '33.3%' }}
          />
        ))}
      </Card>

      <Card label="Invoice To">
        <CardRow
          value={invoiceToOverride?.name || ''}
          placeholder={invoiceTo?.name || ''}
          actionIcon={ICONS.CircleXmarkLight}
          onAction={
            invoiceToOverride
              ? () => handleChangeValue('invoiceToOrganisationId', null, true)
              : undefined
          }
          onPress={() =>
            dispatch(
              NAVIGATE_MODAL({
                screen: ModalScreens.SELECT,
                params: {
                  title: 'Organisations',
                  eventKey: eventKeyOrganisationRef.current,
                  searchable: true,
                  options: sortBy(Object.values(customers), 'name').map(
                    (c) => ({
                      name: c.name,
                      internalValue: c.id,
                    }),
                  ),
                },
              }),
            )
          }
        />
      </Card>
    </PageScrollView>
  );
};
